import React, { useState, Fragment } from 'react'
import { Input } from 'antd'
import {
  useCreateFunction,
  setContextEntry,
  editContextEntry,
  deleteContextEntry,
} from './CreateFunctionContext'
import { ContextField } from './types'
import style from './styles/create-function.module.css'

// icons
import { ReactComponent as PlusIcon } from '../../../../assets/svg/MarketplaceFunctions/addBtn.svg'
import { ReactComponent as DeleteIcon } from '../../../../assets/images/form_icons/MoreOptionsIcons/deleteIcon.svg'
import { ReactComponent as EditIcon } from '../../../../assets/images/form_icons/MoreOptionsIcons/editIcon.svg'
import { ReactComponent as SaveIcon } from '../../../../assets/images/form_icons/MoreOptionsIcons/saveIcon.svg'

interface IData {
  key: string
  value: string
}

interface IUpdate {
  initialData: IData
  idx: number
  field: ContextField
}

const EditDelete: React.FC<IUpdate> = (props) => {
  const iconDimension = 18
  const { initialData, idx, field } = props

  const { dispatch, state } = useCreateFunction()
  const fields = state.context[field]

  const [data, setData] = useState(initialData)
  const [isDisabled, setIsDisabled] = useState(true)
  const [error, setError] = useState('')

  const handleEdit = () => {
    let { key, value } = data
    key = key.trim()
    value = value.trim()

    if (!isDisabled) {
      if (isValid(key, value, idx, fields, setError)) {
        dispatch(editContextEntry({ entry: data, idx, field }))
        setIsDisabled(true)
        setError('')
      }
    } else {
      setIsDisabled(false)
    }
  }

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: 'key' | 'value'
  ) => {
    setError("")
    e.persist()
    setData((prevState) => ({ ...prevState, [key]: e.target.value }))
  }

  return (
    <div>
      <div className={style.editDeleteWrapper}>
        <Input
          placeholder="Enter key"
          style={{ width: '50%' }}
          value={data.key}
          disabled={isDisabled}
          onChange={(e) => handleOnChange(e, 'key')}
        />
        <Input
          placeholder="Enter value"
          style={{ width: '50%' }}
          value={data.value}
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              e.currentTarget.blur()
              handleEdit()
            }
          }}
          disabled={isDisabled}
          onChange={(e) => handleOnChange(e, 'value')}
        />
        <div onClick={handleEdit}>
          {isDisabled ? (
            <EditIcon height={iconDimension} width={iconDimension} />
          ) : (
            <SaveIcon height={iconDimension} width={iconDimension} />
          )}
        </div>
        <DeleteIcon
          width={iconDimension}
          height={iconDimension}
          onClick={() => dispatch(deleteContextEntry({ field, idx }))}
        />
      </div>
      {error && <p className={style.errMsg}>{error}</p>}
    </div>
  )
}

interface IAdd {
  field: ContextField
}

const Add: React.FC<IAdd> = (props) => {
  const { field } = props
  const { dispatch, state } = useCreateFunction()
  const fields = state.context[field]

  const [data, setData] = useState({
    key: '',
    value: '',
  })
  const [error, setError] = useState('')

  const handleAdd = () => {
    let { key, value } = data
    key = key.trim()
    value = value.trim()

    if (isValid(key, value, -1, fields, setError)) {
      dispatch(setContextEntry({ field, entry: data }))
      clearFields()
      setError('')
    }
  }

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: 'key' | 'value'
  ) => {
    setError('')
    e.persist()
    setData((prevState) => ({ ...prevState, [key]: e.target.value }))
  }

  const clearFields = () => {
    setData({ key: '', value: '' })
  }

  return (
    <Fragment>
      <div className={style.addWrapper}>
        <div className={style.addInputSet}>
          <p>Key</p>
          <Input
            placeholder="Enter key"
            value={data.key}
            onChange={(e) => handleOnChange(e, 'key')}
          />
        </div>
        <div className={style.addInputSet}>
          <p>Value</p>
          <Input
            placeholder="Enter value"
            value={data.value}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                e.currentTarget.blur()
                handleAdd()
              }
            }}
            onChange={(e) => handleOnChange(e, 'value')}
          />
        </div>
        <PlusIcon width={28} height={28} onClick={handleAdd} />
      </div>
      {error && <p className={style.errMsg}>{error}</p>}
    </Fragment>
  )
}

interface IAddKeyValue {
  field: ContextField
  title?: string
}

export const AddKeyValue: React.FC<IAddKeyValue> = (props) => {
  const { title, field } = props
  const { state } = useCreateFunction()
  const entries = state.context[field]

  return (
    <div className={style.spacingHorizontal}>
      {title && <h4 className={style.title}>{title}</h4>}

      <Add field={field} />

      {entries.length ? <hr style={{ margin: '20px 0' }} /> : null}

      <div className={style.entriesWrapper}>
        {entries.map((entry, idx) => {
          const [key, value] = entry
          const data = {
            key,
            value,
          }
          return (
            <EditDelete key={key} idx={idx} field={field} initialData={data} />
          )
        })}
      </div>
    </div>
  )
}

// helpers
const isValid = (
  key: string,
  value: string,
  idx: number,
  fields: [string, string][],
  setError: (err: string) => void
) => {
  // empty values
  if (!key || !value) {
    setError('Empty key/value found')
    return false
  }

  // duplicate keys
  const found = fields.find((field, fieldIdx) => {
    const [fieldKey] = field
    return fieldKey === key && fieldIdx !== idx
  })
  if (found) {
    setError('Key already exists')
    return false
  }

  return true
}
